import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tag, Plus, Pencil, X, RefreshCw, Copy, List, Table, Save,
  GripVertical, FilterX, ListFilter
} from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useNotifications } from '../../context/NotificationContext';
import { integrationConfigApi } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import { NO_MATCH_LIST, SEARCH_HINT } from '../../utils/listSearch';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { useListTrunk } from '../../hooks/useListTrunk';
import NoMatchState from '../../components/common/NoMatchState';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import StuiSelect from '../../components/common/StuiSelect';

// ─── Field definitions ────────────────────────────────────────────────────────

const FIELDS = [
  { key: 'plcDatatype',    label: 'PLC Datatype',    required: true,  width: 140, span: 8  },
  { key: 'plcDatamember',  label: 'PLC Datamember',  required: true,  width: 160, span: 8  },
  { key: 'datamemberDesc', label: 'Description',     required: false, width: 220, span: 8  },
  { key: 'tool2Custom',    label: 'Tool2 Custom',    required: false, width: 140, span: 8  },
  { key: 'access',         label: 'Access',          required: false, width: 80,  span: 4  },
  { key: 'ptType',         label: 'PT Type',         required: false, width: 80,  span: 4  },
  { key: 'almClass',       label: 'Alm Class',       required: false, width: 90,  span: 4  },
  { key: 'almEnable',      label: 'Alm Enable',      required: false, width: 90,  span: 4  },
  { key: 'almHigh1',       label: 'Alm High 1',      required: false, width: 90,  span: 4  },
  { key: 'almHigh2',       label: 'Alm High 2',      required: false, width: 90,  span: 4  },
  { key: 'almLow1',        label: 'Alm Low 1',       required: false, width: 90,  span: 4  },
  { key: 'almLow2',        label: 'Alm Low 2',       required: false, width: 90,  span: 4  },
  { key: 'almStr',         label: 'Alm Str',         required: false, width: 80,  span: 4  },
  { key: 'dispLimHigh',    label: 'Disp Lim High',   required: false, width: 100, span: 4  },
  { key: 'dispLimLow',     label: 'Disp Lim Low',    required: false, width: 100, span: 4  },
  { key: 'setpointHigh',   label: 'Setpoint High',   required: false, width: 100, span: 4  },
  { key: 'setpointLow',    label: 'Setpoint Low',    required: false, width: 100, span: 4  },
  { key: 'dataLog',        label: 'Data Log',        required: false, width: 80,  span: 4  },
  { key: 'alarmConfig',    label: 'Alarm Config',    required: false, width: 120, span: 8  },
];

const GRID_COLUMNS = FIELDS.map(f => ({ key: f.key, fieldName: f.key, label: f.label, width: f.width }));
// Derived from FIELDS so a new column is searchable the moment it is declared. The old predicate
// named three of these by hand, leaving the rest of the cross-reference unsearchable.
const SEARCH_FIELDS = FIELDS.map(f => f.key);
const EMPTY_FORM   = Object.fromEntries(FIELDS.map(f => [f.key, '']));
const toSaveDto    = (form) => Object.fromEntries(FIELDS.map(f => [f.key, form[f.key] || null]));
const fromRow      = (row)  => Object.fromEntries(FIELDS.map(f => [f.key, row[f.key] ?? '']));

// ─── Detail form — used in list view and add-new modal ────────────────────────

const DetailForm = ({ form, setForm, readOnly }) => {
  const field = (f) => (
    <div key={f.key} className={`stui-field-group stui-col-${f.span}`}>
      <label>{f.label}{f.required && <span className="stui-required">*</span>}</label>
      <input
        className="stui-field-input"
        value={form[f.key]}
        onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
        readOnly={readOnly}
      />
    </div>
  );
  return (
    <div className="stui-detail-grid">
      <div className="stui-col-24"><div className="stui-detail-section-header">Identification</div></div>
      {FIELDS.slice(0, 3).map(field)}
      <div className="stui-col-24"><div className="stui-detail-section-header">Tag Configuration</div></div>
      {FIELDS.slice(3, 6).map(field)}
      <div className="stui-col-24"><div className="stui-detail-section-header">Alarm Configuration</div></div>
      {FIELDS.slice(6, 14).map(field)}
      <div className="stui-col-24"><div className="stui-detail-section-header">Display &amp; Logging</div></div>
      {FIELDS.slice(14).map(field)}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const PlcTagXRefPage = () => {
  const { dbSlug: slug } = useDatabase();
  const { notify } = useNotifications();

  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewMode, setViewMode] = useState('list');

  // Search + action menu
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen]     = useState(false);
  const menuRef = useRef(null);

  // List view state
  const [picked, setSelected]   = useState(null);
  const [gridSort, setGridSort] = useState(null); // header sort, shared by both views
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [isNew, setIsNew]       = useState(false);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);

  // Datasheet inline edit state
  const [editModeEnabled, setEditModeEnabled]     = useState(false);
  const [editedData, setEditedData]               = useState({});
  const [isDatasheetSaving, setIsDatasheetSaving] = useState(false);
  const originalDataRef = useRef([]);

  // Datasheet filters
  const [filters, setFilters] = useState({});

  // Add-new modal (datasheet mode only)
  const [showAddModal, setShowAddModal]     = useState(false);
  const [addForm, setAddForm]               = useState(EMPTY_FORM);
  const [addModalSaving, setAddModalSaving] = useState(false);

  // Copy-from modal
  const [showCopy, setShowCopy]             = useState(false);
  const [copySlugs, setCopySlugs]           = useState([]);
  const [copySourceSlug, setCopySourceSlug] = useState('');
  const [copyRows, setCopyRows]             = useState([]);
  const [copySelected, setCopySelected]     = useState([]);
  const [copySaving, setCopySaving]         = useState(false);

  // ── Click-outside for action menu ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load ─────────────────────────────────────────────────────────────────────

  const loadRows = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await integrationConfigApi.getTagXRef(slug);
      setRows(res.data);
    } catch {
      notify.error('Failed to load tag xref');
    } finally {
      setLoading(false);
    }
  }, [slug, notify]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // ── List view ─────────────────────────────────────────────────────────────────

  const handleSelect = (row) => {
    setSelected(row);
    setForm(fromRow(row));
    setEditing(false);
    setIsNew(false);
  };

  const handleNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setIsNew(true);
    setEditing(true);
  };

  const handleCancel = () => {
    if (selected) { setForm(fromRow(selected)); setEditing(false); setIsNew(false); }
    else { setIsNew(false); setEditing(false); setForm(EMPTY_FORM); }
  };

  const handleSave = async () => {
    if (!form.plcDatatype?.trim() || !form.plcDatamember?.trim()) {
      notify.warning('PLC Datatype and Datamember are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await integrationConfigApi.addTagXRef(slug, toSaveDto(form));
        setRows(prev => [...prev, res.data]);
        setSelected(res.data);
        setForm(fromRow(res.data));
        setIsNew(false);
        setEditing(false);
        notify.success('Row added');
      } else {
        const res = await integrationConfigApi.updateTagXRef(slug, selected.id, toSaveDto(form));
        setRows(prev => prev.map(r => r.id === selected.id ? res.data : r));
        setSelected(res.data);
        setForm(fromRow(res.data));
        setEditing(false);
        notify.success('Row updated');
      }
    } catch {
      notify.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (target) => {
    if (!target) return;
    if (!window.confirm(`Delete ${target.plcDatatype}.${target.plcDatamember}?`)) return;
    try {
      await integrationConfigApi.deleteTagXRef(slug, target.id);
      setRows(prev => prev.filter(r => r.id !== target.id));
      if (selected?.id === target.id) { setSelected(null); setForm(EMPTY_FORM); setEditing(false); }
      notify.success('Row deleted');
    } catch {
      notify.error('Delete failed');
    }
  };

  // ── Datasheet inline editing ───────────────────────────────────────────────

  const enterEditMode = () => {
    originalDataRef.current = [...rows];
    setEditedData({});
    setEditModeEnabled(true);
  };

  const handleDatasheetCancel = () => {
    setEditedData({});
    setEditModeEnabled(false);
  };

  const handleCellChange = useCallback((rowId, fieldKey, value) => {
    setEditedData(prev => {
      const rowEdits = prev[rowId] || {};
      const originalRow = originalDataRef.current.find(r => r.id === rowId);
      const originalValue = originalRow?.[fieldKey] ?? '';
      const normalized = value ?? '';
      const normalizedOrig = originalValue === null || originalValue === undefined ? '' : String(originalValue);

      if (normalized === normalizedOrig) {
        const { [fieldKey]: _, ...remaining } = rowEdits;
        if (Object.keys(remaining).length === 0) {
          const { [rowId]: __, ...remainingRows } = prev;
          return remainingRows;
        }
        return { ...prev, [rowId]: remaining };
      }
      return { ...prev, [rowId]: { ...rowEdits, [fieldKey]: value } };
    });
  }, []);

  const handleDatasheetSave = async () => {
    const dirtyRowIds = Object.keys(editedData).map(Number);
    if (!dirtyRowIds.length) { setEditModeEnabled(false); return; }
    setIsDatasheetSaving(true);
    try {
      await Promise.all(dirtyRowIds.map(rowId => {
        const original = originalDataRef.current.find(r => r.id === rowId);
        const changes = editedData[rowId];
        const merged = { ...fromRow(original), ...changes };
        return integrationConfigApi.updateTagXRef(slug, rowId, toSaveDto(merged));
      }));
      await loadRows();
      setEditedData({});
      setEditModeEnabled(false);
      notify.success(`Saved ${dirtyRowIds.length} row(s)`);
    } catch {
      notify.error('Save failed');
    } finally {
      setIsDatasheetSaving(false);
    }
  };

  const renderEditableCell = useCallback((row, col, value, isEdited) => {
    if (isDatasheetSaving) return <span>{value ?? ''}</span>;
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={e => handleCellChange(row.id, col.key, e.target.value)}
      />
    );
  }, [isDatasheetSaving, handleCellChange]);

  // ── Add-new modal (datasheet mode) ────────────────────────────────────────────

  const openAddModal = () => {
    setAddForm(EMPTY_FORM);
    setShowAddModal(true);
  };

  const handleAddModalSave = async () => {
    if (!addForm.plcDatatype?.trim() || !addForm.plcDatamember?.trim()) {
      notify.warning('PLC Datatype and Datamember are required');
      return;
    }
    setAddModalSaving(true);
    try {
      const res = await integrationConfigApi.addTagXRef(slug, toSaveDto(addForm));
      setRows(prev => [...prev, res.data]);
      setShowAddModal(false);
      notify.success('Row added');
    } catch {
      notify.error('Save failed');
    } finally {
      setAddModalSaving(false);
    }
  };

  // ── Copy-from ─────────────────────────────────────────────────────────────────

  const openCopyModal = async () => {
    try {
      const res = await integrationConfigApi.getSlugs();
      setCopySlugs(res.data.filter(s => s !== slug));
      setCopySourceSlug('');
      setCopyRows([]);
      setCopySelected([]);
      setShowCopy(true);
    } catch {
      notify.error('Failed to load slugs');
    }
  };

  const handleCopySlugChange = async (sourceSlug) => {
    setCopySourceSlug(sourceSlug);
    setCopyRows([]);
    setCopySelected([]);
    if (!sourceSlug) return;
    try {
      const res = await integrationConfigApi.getTagXRefFrom(slug, sourceSlug);
      setCopyRows(res.data);
    } catch {
      notify.error('Failed to load source rows');
    }
  };

  const handleExecuteCopy = async () => {
    if (!copySelected.length) return;
    const xRefIds = copyRows.filter(r => copySelected.includes(r.plcDatatype)).map(r => r.id);
    setCopySaving(true);
    try {
      await integrationConfigApi.copyTagXRef(slug, { sourceSlug: copySourceSlug, xRefIds });
      await loadRows();
      setShowCopy(false);
      notify.success(`Copied ${copySelected.length} datatype(s) — ${xRefIds.length} row(s)`);
    } catch {
      notify.error('Copy failed');
    } finally {
      setCopySaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  // One reduction for both views. `filters` was declared and handed to the grid but never applied
  // here, so the grid narrowed itself in private and list view never heard about it.
  // One trunk (sort -> search -> filters) for both views. See hooks/useListTrunk. A new record in
  // progress has no row yet, so nothing is selected while it is open.
  const trunk = useListTrunk({
    rows, query: searchTerm, searchFields: SEARCH_FIELDS, labels: FIELDS,
    filters, sortConfig: gridSort, picked, keep: editing,
  });
  const searchedRows = trunk.searched;
  const filteredRows = trunk.filtered;
  const hasFilters   = trunk.hasFilters;
  const selected = isNew ? null : trunk.selected;

  // The pane renders `form`, not the row, so it has to follow when search or filters move the
  // selection. An open edit keeps its form; resolveSelection holds that row on screen anyway.
  const selectedId = selected?.id;
  useEffect(() => {
    if (editing || isNew) return;
    setForm(selected ? fromRow(selected) : EMPTY_FORM);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="stui-module-container">

      {/* HEADER */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <div className="stui-module-header-tabs">
            <button onClick={() => { setViewMode('list'); handleDatasheetCancel(); }} className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
        </div>
        <div className="stui-module-header-actions">

          {/* CRUD — list view */}
          {viewMode === 'list' && (
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" onClick={handleNew} title="Add New Record"><Plus size={18} /></button>
              <button onClick={editing ? handleCancel : () => setEditing(true)} className={`stui-crud-btn ${!editing && !selected ? 'disabled' : ''}`} disabled={!editing && !selected} title={editing ? 'Cancel' : 'Edit Record'}>
                {editing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!editing ? 'disabled' : ''}`} onClick={handleSave} disabled={!editing || saving} title="Save Changes"><Save size={18} /></button>
            </div>
          )}

          {/* CRUD — datasheet view */}
          {viewMode === 'datasheet' && !editModeEnabled && (
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" onClick={openAddModal} title="Add New Record"><Plus size={18} /></button>
              <button className="stui-crud-btn" onClick={enterEditMode} title="Edit Inline"><Pencil size={18} /></button>
            </div>
          )}
          {viewMode === 'datasheet' && editModeEnabled && (
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" onClick={handleDatasheetSave} disabled={isDatasheetSaving} title="Save Changes"><Save size={18} /></button>
              <button className="stui-crud-btn" onClick={handleDatasheetCancel} disabled={isDatasheetSaving} title="Cancel"><X size={18} /></button>
            </div>
          )}

          {/* Clear filter — the page had no way to undo a column filter except reopening
              each dropdown, so an active filter could silently hide rows indefinitely. */}
          {hasFilters && (
            <button onClick={() => setFilters({})} title="Clear all filters" className="stui-clear-filter-btn">
              <FilterX size={16} /><span>{filteredRows.length}/{rows.length}</span>
            </button>
          )}

          {/* Search */}
          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search..."
              title={SEARCH_HINT}
              className="stui-field-input"
            />
          </div>

          {/* Action menu (6-dot) */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadRows(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); openCopyModal(); }}>
                  <Copy size={16} /><span>Copy from Database…</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="stui-list-view">
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <span className="stui-label">
                {filteredRows.length}{filteredRows.length !== rows.length ? `/${rows.length}` : ''} row{rows.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="stui-list-scroll">
              {loading ? (
                <div className="stui-list-empty">Loading...</div>
              ) : filteredRows.length === 0 ? (
                <div className="stui-list-empty">{rows.length === 0 ? 'No rows — click + to add' : NO_MATCH_LIST}</div>
              ) : filteredRows.map(r => (
                <button key={r.id} onClick={() => handleSelect(r)} className={`stui-list-item ${selected?.id === r.id ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">{r.plcDatatype}</span>
                  <span className="stui-list-item-secondary">{r.plcDatamember}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="stui-detail-panel">
            {(selected || isNew) ? (
              <div className="stui-detail-content">
                <DetailForm form={form} setForm={setForm} readOnly={!editing} />
                {selected && editing && (
                  <div className="stui-btn-row">
                    <button className="stui-btn stui-btn-danger stui-btn-sm" onClick={() => handleDelete(selected)}>Delete</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="stui-detail-empty">
                {rows.length > 0 && filteredRows.length === 0 ? (
                  <NoMatchState hasSearch={!!searchTerm} hasFilters={hasFilters} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setFilters({})} />
                ) : (
                  <>
                    <Tag size={32} />
                    <p>Select a row or click + to create one</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DATASHEET VIEW */}
      {viewMode === 'datasheet' && (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={filteredRows}
            allData={searchedRows}
            sortConfig={gridSort || { key: null, direction: null }}
            onSortChange={setGridSort}
            columns={GRID_COLUMNS}
            filters={filters}
            setFilters={setFilters}
            columnWidths={{}}
            editModeEnabled={editModeEnabled}
            editedData={editedData}
            renderEditableCell={renderEditableCell}
          />
        </div>
      )}

      {/* ADD-NEW MODAL (datasheet mode) */}
      {showAddModal && (
        <div className="stui-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="stui-modal stui-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="stui-modal-header">
              <h3>Add Tag XRef</h3>
              <button className="stui-modal-close" onClick={() => setShowAddModal(false)}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <DetailForm form={addForm} setForm={setAddForm} readOnly={false} />
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddModalSave} disabled={addModalSaving}>
                {addModalSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COPY-FROM MODAL */}
      {showCopy && (
        <div className="stui-modal-overlay" onClick={() => setShowCopy(false)}>
          <div className="stui-modal stui-modal-md" onClick={e => e.stopPropagation()}>
            <div className="stui-modal-header">
              <h3>Copy Tag XRef From Another Database</h3>
              <button className="stui-modal-close" onClick={() => setShowCopy(false)}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-field-group">
                <label>Source Database</label>
                <StuiSelect className="stui-field-input" value={copySourceSlug} onChange={e => handleCopySlugChange(e.target.value)}>
                  <option value="">— Select —</option>
                  {copySlugs.map(s => <option key={s} value={s}>{s}</option>)}
                </StuiSelect>
              </div>
              {copyRows.length > 0 && (() => {
                const distinctTypes = [...new Set(copyRows.map(r => r.plcDatatype))].sort();
                const memberCount = copySelected.reduce((n, dt) => n + copyRows.filter(r => r.plcDatatype === dt).length, 0);
                return (
                  <div>
                    <label className="stui-label">
                      Select datatypes to copy (all datamembers included):
                    </label>
                    <div className="stui-checkbox-list">
                      {distinctTypes.map(dt => {
                        const count = copyRows.filter(r => r.plcDatatype === dt).length;
                        return (
                          <label key={dt} className="stui-checkbox-list-item">
                            <input type="checkbox" className="stui-checkbox-input" checked={copySelected.includes(dt)} onChange={e => setCopySelected(prev => e.target.checked ? [...prev, dt] : prev.filter(d => d !== dt))} />
                            <span className="stui-checkbox-list-item-name">{dt}</span>
                            <span className="stui-checkbox-list-item-count">{count} member{count !== 1 ? 's' : ''}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="stui-btn-row">
                      <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setCopySelected(distinctTypes)}>Select All</button>
                      <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setCopySelected([])}>Clear</button>
                    </div>
                    {memberCount > 0 && <p className="stui-hint">{memberCount} total row{memberCount !== 1 ? 's' : ''} will be copied</p>}
                  </div>
                );
              })()}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowCopy(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleExecuteCopy} disabled={!copySelected.length || copySaving}>
                {copySaving ? 'Copying...' : `Copy ${copySelected.length || ''} Datatype${copySelected.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={FIELDS} onApply={setSearchTerm} />
    </div>
  );
};

export default PlcTagXRefPage;
